package com.example.mystybag.core.expected

import kotlinx.browser.window

internal actual object PreferencesImpl {
    private const val STORAGE_PREFIX = "mystybag_pref:"
    private val store = mutableMapOf<String, Any?>()
    private val storage = runCatching { window.localStorage }.getOrNull()

    private fun storageKey(key: String): String = "$STORAGE_PREFIX$key"

    init {
        loadFromStorage()
    }

    private fun loadFromStorage() {
        val localStorage = storage ?: return
        val keys = mutableListOf<String>()
        val length = localStorage.length
        var index = 0
        while (index < length) {
            val currentKey = localStorage.key(index)
            if (currentKey != null && currentKey.startsWith(STORAGE_PREFIX)) {
                keys.add(currentKey)
            }
            index++
        }

        for (fullKey in keys) {
            val value = localStorage.getItem(fullKey) ?: continue
            val prefKey = fullKey.removePrefix(STORAGE_PREFIX)
            decode(value)?.let { decoded ->
                store[prefKey] = decoded
            }
        }
    }

    private fun persist(key: String, type: String, value: dynamic) {
        store[key] = when (type) {
            "set" -> (value as Array<String>).toSet()
            else -> value
        }

        val localStorage = storage ?: return
        val payload: dynamic = js("({})")
        payload.t = type
        payload.v = value
        localStorage.setItem(storageKey(key), JSON.stringify(payload))
    }

    private fun decode(raw: String): Any? {
        return runCatching {
            val payload = JSON.parse<dynamic>(raw)
            when (payload.t as? String) {
                "int", "float", "long" -> (payload.v as? Number)
                "string" -> payload.v as? String
                "bool" -> payload.v as? Boolean
                "set" -> (payload.v as? Array<*>)?.mapNotNull { it as? String }?.toSet()
                else -> null
            }
        }.getOrNull()
    }

    actual fun setInt(key: String, value: Int) = persist(key, "int", value)
    actual fun getInt(key: String, defaultValue: Int): Int = (store[key] as? Number)?.toInt() ?: defaultValue
    actual fun getInt(key: String): Int? = (store[key] as? Number)?.toInt()

    actual fun setFloat(key: String, value: Float) = persist(key, "float", value)
    actual fun getFloat(key: String, defaultValue: Float): Float = (store[key] as? Number)?.toFloat() ?: defaultValue
    actual fun getFloat(key: String): Float? = (store[key] as? Number)?.toFloat()

    // Store Long as Double so numeric casting remains stable in Kotlin/JS.
    actual fun setLong(key: String, value: Long) = persist(key, "long", value.toDouble())
    actual fun getLong(key: String, defaultValue: Long): Long = (store[key] as? Number)?.toLong() ?: defaultValue
    actual fun getLong(key: String): Long? = (store[key] as? Number)?.toLong()

    actual fun setString(key: String, value: String) = persist(key, "string", value)
    actual fun getString(key: String, defaultValue: String): String = store[key] as? String ?: defaultValue
    actual fun getString(key: String): String? = store[key] as? String

    actual fun setBoolean(key: String, value: Boolean) = persist(key, "bool", value)
    actual fun getBoolean(key: String, defaultValue: Boolean): Boolean = store[key] as? Boolean ?: defaultValue
    actual fun getBoolean(key: String): Boolean? = store[key] as? Boolean

    actual fun getStringSet(key: String, defaultValue: Set<String>?): Set<String>? =
        (store[key] as? Set<*>)?.filterIsInstance<String>()?.toSet() ?: defaultValue

    actual fun setStringSet(key: String, values: Set<String>) = persist(key, "set", values.toTypedArray())

    actual fun remove(key: String) {
        store.remove(key)
        storage?.removeItem(storageKey(key))
    }

    actual fun clear() {
        store.clear()
        val localStorage = storage ?: return
        val keys = mutableListOf<String>()
        val length = localStorage.length
        var index = 0
        while (index < length) {
            val currentKey = localStorage.key(index)
            if (currentKey != null && currentKey.startsWith(STORAGE_PREFIX)) {
                keys.add(currentKey)
            }
            index++
        }
        for (key in keys) {
            localStorage.removeItem(key)
        }
    }

    actual fun hasKey(key: String): Boolean = key in store
}
